import type { EnrollmentStep } from '@dyspatch/core';
import { Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CommandBox, EnrollmentSteps } from '@/features/fleet/ConnectDevice';
import { useProjects } from '@/hooks/use-fleet';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';

/**
 * The trigger, for a view header.
 *
 * "Enroll device" read as "add a machine" to an admin who wanted to add their
 * own, and the form it opens asks for an owner email and a repo list because it
 * is for provisioning somebody else's. Your own laptop is the card on the page;
 * this says whose machine it is for, so the two are not one choice made twice.
 */
export function EnrollButton({
  onClick,
  tone = 'hero',
}: {
  onClick: () => void;
  tone?: 'hero' | 'quiet';
}) {
  return (
    <Button
      size={tone === 'hero' ? 'pill' : 'sm'}
      variant={tone === 'hero' ? 'hero' : 'outline'}
      onClick={onClick}
    >
      <Plus aria-hidden />
      {/* The verb keeps its object where there is room for it. On a phone the
          pill sat against the title and pushed the palette button to the edge;
          "Enroll" beside a plus already says who it is not for, since your own
          machine is connected from the list below. */}
      <span className="sm:hidden">Enroll</span>
      <span className="max-sm:hidden">Enroll someone else</span>
    </Button>
  );
}

/**
 * Minting an invite, in place.
 *
 * Two phases, because the second one is the point: the command that a laptop
 * redeems is a single-use secret somebody has to copy, and a dialog that closes
 * on success is a dialog that eats it. So the form is replaced by the command,
 * with the copy button next to it, and closing is a deliberate act.
 */
export function EnrollComposer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [repos, setRepos] = useState<string[]>([]);
  const [steps, setSteps] = useState<EnrollmentStep[] | null>(null);
  /** The one thing that goes into Slack. See `enrollmentUrl` on the hub. */
  const [url, setUrl] = useState<string | null>(null);
  const first = useRef<HTMLInputElement>(null);
  const projects = useProjects();
  const known = (projects.data ?? []).map((project) => project.repo);

  const create = useHubAction(
    async () => {
      const result = await api.createInvite({
        workerName: name.trim(),
        forEmail: email.trim(),
        repos,
      });
      setSteps(result.steps);
      setUrl(result.url);
      return result;
    },
    // The composer prints the refusal under its own buttons.
    { success: 'Invite created.', inlineError: true },
  );

  useEffect(() => {
    if (open && !steps) first.current?.focus();
  }, [open, steps]);

  const close = () => {
    onClose();
    setSteps(null);
    setUrl(null);
    setName('');
    setEmail('');
    setRepos([]);
  };

  if (!open) return null;

  return (
    <section className="bg-card overflow-hidden rounded-lg border">
      <div className="bg-sunken flex items-center gap-2 border-b px-gutter py-[calc(var(--dy-row-py)+1px)]">
        <h2 className="text-sm font-medium">Enroll someone else's device</h2>
        <span className="text-fg-3 text-2xs">
          one machine, one invite, one use. Your own laptop is on this page.
        </span>
        <Button
          variant="ghost"
          size="icon-xs"
          className="ml-auto"
          onClick={close}
          aria-label="Close"
        >
          <X />
        </Button>
      </div>

      {steps ? (
        <div>
          {url ? (
            <div className="border-b px-gutter py-2.5">
              <p className="text-foreground text-sm font-medium">Send them this link</p>
              <p className="text-fg-3 mb-1.5 text-2xs">
                It opens this same sequence with the code already in it, so nothing has to be
                retyped and the hub address travels with it.
              </p>
              <CommandBox command={url} />
            </div>
          ) : null}
          <p className="text-fg-2 px-gutter py-2.5 text-xs">
            Or send the steps themselves. The code works once, on one machine, and it expires. They
            can also do all of this themselves from Agents.
          </p>
          <EnrollmentSteps steps={steps} />
          <div className="flex items-center gap-1.5 border-t px-gutter py-3">
            <Button size="sm" variant="ghost" onClick={close}>
              Done
            </Button>
          </div>
        </div>
      ) : (
        <form
          className="space-y-2.5 px-gutter py-3"
          onKeyDown={(event) => {
            if (event.key === 'Escape') close();
          }}
          onSubmit={(event) => {
            event.preventDefault();
            create.run();
          }}
        >
          <div className="grid gap-2.5 sm:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="iv-name">Worker name</Label>
              <Input
                ref={first}
                id="iv-name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                placeholder="sam-studio"
              />
              <p className="text-fg-3 text-2xs">Lowercase, no spaces.</p>
            </div>
            <div className="space-y-1">
              <Label htmlFor="iv-email">Owner email</Label>
              <Input
                id="iv-email"
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                placeholder="sam.tripathi@nymbl.app"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="iv-repos">Repos</Label>
              <Combobox
                id="iv-repos"
                values={repos}
                onChange={setRepos}
                options={known}
                placeholder="nymbl-app"
              />
              <p className="text-fg-3 text-2xs">
                Optional. Pick from the repos in flight, or type a new one.
              </p>
            </div>
          </div>

          {create.error ? (
            <p className="text-bad text-xs" role="alert">
              {create.error}
            </p>
          ) : null}

          <div className="flex items-center gap-1.5">
            <Button type="submit" size="sm" disabled={create.busy}>
              {create.busy ? 'Creating…' : 'Create invite'}
            </Button>
            <Button type="button" size="sm" variant="ghost" onClick={close}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </section>
  );
}
